/**
 * usePackingStockLive — keeps packing-material on-hand numbers fresh over WebSocket.
 *
 * Subscribed by the components that actually RENDER the stock (Inventory → Packing
 * Materials and Settings → Packing Materials) rather than by their parent pages, so a
 * view is live wherever it is mounted. Settings in particular never subscribed to any
 * inbound event — it only emitted `config-updated` — so its stock numbers were stale
 * until a manual reload (WCO-2155).
 *
 * The backend emits `packing-replenishment` with a `type` discriminator:
 *   'consumed'  — a box was packed (operator-service confirmBox)
 *   'received'  — a packing transfer was verified (inventory-service transferVerification)
 *   'created'   — a draft replenishment transfer was auto-generated
 *   'cancelled' — a draft replenishment alert was deleted
 * All of them can change what these tables show, so all of them invalidate.
 *
 * Reuses the existing event name instead of adding new ones: the api-gateway only
 * relays events on its explicit allowlist (`api-gateway/src/index.ts`), so a new name
 * would silently never arrive unless registered there too.
 */
import { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { wsService } from '../services/websocket';

export function usePackingStockLive(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const handlePackingEvent = () => {
      queryClient.invalidateQueries(['packing-thresholds']);
      queryClient.invalidateQueries(['packing-replenishment-alerts']);
    };

    wsService.on('packing-replenishment', handlePackingEvent);
    return () => {
      wsService.off('packing-replenishment', handlePackingEvent);
    };
  }, [queryClient]);
}

export default usePackingStockLive;
